import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Calendar, List, Plus, UserCircle, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navigation from "@/components/Navigation";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [crewLoggedIn, setCrewLoggedIn] = useState(false);
  const [crewName, setCrewName] = useState("");

  useEffect(() => {
    loadEvents();
    checkSessions();
  }, []);

  const checkSessions = () => {
    // Check admin session
    const adminToken = localStorage.getItem('adminToken');
    const adminData = localStorage.getItem('adminData');
    if (adminToken && adminData) {
      try {
        const data = JSON.parse(adminData);
        setAdminLoggedIn(true);
        setAdminEmail(data.email || "Admin");
      } catch (e) {
        setAdminLoggedIn(false);
      }
    }
    
    // Check crew session
    const crewToken = localStorage.getItem('crewToken');
    const crewData = localStorage.getItem('crewData');
    if (crewToken && crewData) {
      try {
        const data = JSON.parse(crewData);
        setCrewLoggedIn(true);
        setCrewName(data.preferred_name || data.name || "Crew Member");
      } catch (e) {
        setCrewLoggedIn(false);
      }
    }
  };

  const handleAdminLogout = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (token) {
        await axios.post(`${API}/admin/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error("Logout API error:", error);
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    setAdminLoggedIn(false);
    setAdminEmail("");
    toast.success("Logged out successfully");
  };

  const handleCrewLogout = async () => {
    try {
      const token = localStorage.getItem('crewToken');
      if (token) {
        await axios.post(`${API}/crew/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error("Logout API error:", error);
    }
    localStorage.removeItem('crewToken');
    localStorage.removeItem('crewData');
    setCrewLoggedIn(false);
    setCrewName("");
    toast.success("Logged out successfully");
  };

  const loadEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Failed to load events:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCalendarDays = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.date === dateStr);
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const currentMonth = monthNames[new Date().getMonth()];
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-crew-black relative grain-overlay">
      <Navigation userType="public" />
      
      {/* Hero Section */}
      <div 
        className="relative min-h-[calc(100vh-5rem)] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1761053703248-d578c6c09ab5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHx0aGVhdGVyJTIwYmFja3N0YWdlJTIwY3JldyUyMHN0YWdlJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzY0OTcxODQ5fDA&ixlib=rb-4.1.0&q=85')"
        }}
      >
        <div className="absolute inset-0 bg-black/85"></div>
        <div className="relative z-10 w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <div className="max-w-4xl mx-auto text-center mb-16">
              {/* Admin Logged In Banner */}
              {adminLoggedIn && (
                <div className="mb-8 flex items-center justify-center gap-4 flex-wrap" data-testid="admin-logged-in-banner">
                  <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800/80 rounded-sm border border-green-500/50">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-base text-zinc-200">Admin: <span className="text-crew-yellow font-bold">{adminEmail}</span></span>
                  </div>
                  <Button
                    onClick={handleAdminLogout}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500"
                    data-testid="home-admin-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </Button>
                  <Link to="/admin/dashboard">
                    <Button className="bg-crew-yellow text-black font-bold hover:bg-white" data-testid="go-to-dashboard">
                      Go to Dashboard
                    </Button>
                  </Link>
                </div>
              )}
              
              {/* Crew Logged In Banner */}
              {crewLoggedIn && !adminLoggedIn && (
                <div className="mb-8 flex items-center justify-center gap-4 flex-wrap" data-testid="crew-logged-in-banner">
                  <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800/80 rounded-sm border border-crew-yellow/50">
                    <div className="w-2 h-2 bg-crew-yellow rounded-full animate-pulse"></div>
                    <span className="text-base text-zinc-200">Crew: <span className="text-crew-yellow font-bold">{crewName}</span></span>
                  </div>
                  <Button
                    onClick={handleCrewLogout}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500"
                    data-testid="home-crew-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </Button>
                  <Link to="/crew/dashboard">
                    <Button className="bg-crew-yellow text-black font-bold hover:bg-white" data-testid="go-to-crew-dashboard">
                      Go to My Events
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Three Main Options - Reordered: Crew Access, Join, Admin */}
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto">
              {/* 1. Crew Member Access (First) */}
              <Link 
                to={crewLoggedIn ? "/crew/dashboard" : "/crew/login"}
                className="bg-crew-zinc border-2 border-zinc-700 hover:border-crew-yellow p-6 md:p-8 rounded-sm transition-all hover:scale-105 group block"
                data-testid="crew-card"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-zinc-800 rounded-sm flex items-center justify-center mb-4 md:mb-6 group-hover:bg-crew-yellow transition-colors">
                  <svg className="w-6 h-6 md:w-8 md:h-8 text-zinc-300 group-hover:text-black transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3 uppercase tracking-tight">
                  Crew Member Access
                </h3>
                <p className="text-zinc-300 text-sm md:text-base mb-3 md:mb-4">
                  {crewLoggedIn
                    ? "You're signed in. Go straight to your dashboard to view events and confirm attendance."
                    : "Already registered and approved? Access your dashboard to view events and confirm attendance."}
                </p>
                <div className="text-crew-yellow text-sm md:text-base font-bold uppercase flex items-center">
                  {crewLoggedIn ? "Go to My Events" : "Sign In"}
                  <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>

              {/* 2. Join Our Crew (Second) */}
              <Link 
                to="/register"
                className="bg-crew-zinc border-2 border-zinc-700 hover:border-crew-yellow p-6 md:p-8 rounded-sm transition-all hover:scale-105 group block"
                data-testid="register-card"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-zinc-800 rounded-sm flex items-center justify-center mb-4 md:mb-6 group-hover:bg-crew-yellow transition-colors">
                  <svg className="w-6 h-6 md:w-8 md:h-8 text-zinc-300 group-hover:text-black transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3 uppercase tracking-tight">
                  Join Our Crew
                </h3>
                <p className="text-zinc-300 text-sm md:text-base mb-3 md:mb-4">
                  New to Crew Call? Apply here to join the crew. Your application will be reviewed by administrators.
                </p>
                <div className="text-crew-yellow text-sm md:text-base font-bold uppercase flex items-center">
                  Apply Now
                  <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>

              {/* Admin login is available via the "Admin" link in the top navigation */}
            </div>

            {/* Info Section */}
            <div className="max-w-3xl mx-auto mt-16 text-center">
              <div className="bg-crew-zinc border border-zinc-800 p-6 rounded-sm">
                <h4 className="text-crew-yellow font-bold text-lg mb-2 uppercase">How It Works</h4>
                <p className="text-zinc-300 text-base">
                  <span className="text-white">1.</span> Apply to join the crew → 
                  <span className="text-white"> 2.</span> Wait for administrator approval → 
                  <span className="text-white"> 3.</span> Receive email notification → 
                  <span className="text-white"> 4.</span> Access crew dashboard to view and confirm events
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
